import {
  type FC,
  lazy,
  Suspense,
  type SVGAttributes,
  type SVGProps,
} from "react";

const logo = lazy(() => import("./svg/logo.svg?react"));
const darkLogo = lazy(() => import("./svg/dark-logo.svg?react"));
const search = lazy(() => import("./svg/search.svg?react"));
const copy = lazy(() => import("./svg/copy.svg?react"));
const copyIcon = lazy(() => import("./svg/copyIcon.svg?react"));
const ethereum = lazy(() => import("./svg/ethereum.svg?react"));
const ethereum2 = lazy(() => import("./svg/ethereum2.svg?react"));
const solanaLogo = lazy(() => import("./svg/solana-logo.svg?react"));
const list = lazy(() => import("./svg/list.svg?react"));
const swapHorizontal = lazy(() => import("./svg/swap-horizontal.svg?react"));
const lineChart = lazy(() => import("./svg/line-chart.svg?react"));
const filter = lazy(() => import("./svg/filter.svg?react"));
const filterGradient = lazy(() => import("./svg/filter-gradient.svg?react"));
const filterWhite = lazy(() => import("./svg/filter-white.svg?react"));
const check = lazy(() => import("./svg/check.svg?react"));
const checkWhite = lazy(() => import("./svg/check-white.svg?react"));
const dollarSign = lazy(() => import("./svg/dollar-sign.svg?react"));
const solana = lazy(() => import("./svg/solana.svg?react"));
const eye = lazy(() => import("./svg/eye.svg?react"));
const walletInputSymbol = lazy(
  () => import("./svg/wallet-input-symbol.svg?react")
);
const hour = lazy(() => import("./svg/hour.svg?react"));
const percentage = lazy(() => import("./svg/percentage.svg?react"));
const uniswap = lazy(() => import("./svg/uniswap.svg?react"));
const raydium = lazy(() => import("./svg/raydium.svg?react"));
const orca = lazy(() => import("./svg/orca.svg?react"));
const meteora = lazy(() => import("./svg/meteora.svg?react"));
const fluxbeam = lazy(() => import("./svg/flux-beam.svg?react"));
const launchlab = lazy(() => import("./svg/launchlab.svg?react"));
const boop = lazy(() => import("./svg/boop.svg?react"));
const oneintro = lazy(() => import("./svg/1intro.svg?react"));
const dexlab = lazy(() => import("./svg/dexlab.svg?react"));
const internetPlanet = lazy(() => import("./svg/internet-planet.svg?react"));
const socialTelegram = lazy(() => import("./svg/social-telegram.svg?react"));
const socialX = lazy(() => import("./svg/social-x.svg?react"));
const xComments = lazy(() => import("./svg/x-comments.svg?react"));
const xReposts = lazy(() => import("./svg/x-reposts.svg?react"));
const xLikes = lazy(() => import("./svg/x-likes.svg?react"));
const xViews = lazy(() => import("./svg/x-views.svg?react"));
const xBookmarks = lazy(() => import("./svg/x-bookmarks.svg?react"));
const xPremium = lazy(() => import("./svg/x-premium.svg?react"));
const starEmpty = lazy(() => import("./svg/star-empty.svg?react"));
const starFull = lazy(() => import("./svg/star-full.svg?react"));
const back = lazy(() => import("./svg/back-return.svg?react"));
const markPurple = lazy(() => import("./svg/caution-mark-purple.svg?react"));
const markBlue = lazy(() => import("./svg/caution-mark-blue.svg?react"));
const x = lazy(() => import("./svg/x.svg?react"));
const circleGreen = lazy(() => import("./svg/circle-green.svg?react"));
const circleYellow = lazy(() => import("./svg/circle-yellow.svg?react"));
const info = lazy(() => import("./svg/info.svg?react"));
const human = lazy(() => import("./svg/human.svg?react"));
const path = lazy(() => import("./svg/path.svg?react"));
const pump = lazy(() => import("./svg/pump.svg?react"));
const pumpAmm = lazy(() => import("./svg/pumpamm.svg?react"));
const settings = lazy(() => import("./svg/settings.svg?react"));
const trash = lazy(() => import("./svg/trash.svg?react"));
const key = lazy(() => import("./svg/key.svg?react"));
const arrowDown = lazy(() => import("./svg/arrow-down.svg?react"));
const arrowUp = lazy(() => import("./svg/arrow-up.svg?react"));
const gradientCircle = lazy(() => import("./svg/gradient-circle.svg?react"));
const grayCircle = lazy(() => import("./svg/gray-circle.svg?react"));
const emptyCircle = lazy(() => import("./svg/empty-circle.svg?react"));
const xError = lazy(() => import("./svg/x-error.svg?react"));
const xCheck = lazy(() => import("./svg/x-check.svg?react"));
const barChart = lazy(() => import("./svg/bar-chart.svg?react"));
const send = lazy(() => import("./svg/send.svg?react"));
const close = lazy(() => import("./svg/close.svg?react"));
const link = lazy(() => import("./svg/link.svg?react"));
const switchIcon = lazy(() => import("./svg/switch.svg?react"));
const turbo = lazy(() => import("./svg/turbo.svg?react"));
const turboBlue = lazy(() => import("./svg/turbo-blue.svg?react"));
const turboGreen = lazy(() => import("./svg/turbo-green.svg?react"));
const editPencil = lazy(() => import("./svg/edit-pencil.svg?react"));

const ICONS_MAP = {
  logo,
  darkLogo,
  search,
  copy,
  copyIcon,
  ethereum,
  ethereum2,
  solanaLogo,
  list,
  swapHorizontal,
  lineChart,
  filter,
  filterGradient,
  filterWhite,
  check,
  checkWhite,
  dollarSign,
  solana,
  eye,
  walletInputSymbol,
  hour,
  percentage,
  uniswap,
  launchlab,
  boop,
  raydium,
  orca,
  meteora,
  fluxbeam,
  oneintro,
  dexlab,
  internetPlanet,
  socialTelegram,
  socialX,
  xComments,
  xReposts,
  xLikes,
  xViews,
  xBookmarks,
  xPremium,
  starEmpty,
  starFull,
  back,
  markBlue,
  markPurple,
  x,
  circleGreen,
  circleYellow,
  info,
  human,
  path,
  pump,
  pumpAmm,
  settings,
  trash,
  key,
  arrowDown,
  arrowUp,
  gradientCircle,
  grayCircle,
  emptyCircle,
  xError,
  xCheck,
  barChart,
  send,
  close,
  link,
  switchIcon,
  turbo,
  turboBlue,
  editPencil,
  turboGreen,
} as const;

export type IconsTypes = keyof typeof ICONS_MAP;

export interface IIconComponentProperties extends SVGAttributes<SVGElement> {
  name: IconsTypes;
}

export const IconComponent = ({ name, ...props }: IIconComponentProperties) => {
  const Icon = ICONS_MAP[name] as FC<SVGProps<SVGSVGElement>>;
  if (!Icon) return null;

  return (
    <Suspense fallback={null}>
      <Icon role="img" {...props} />
    </Suspense>
  );
};
